import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { ArrowBack, Close, Add, Block, HelpOutline } from '@mui/icons-material';
import './GenConfPanel.css';
import { GROUPED_TYPES, TYPE_FILTER_ORDER, CAT } from '../../core/buildUtils';
import TypeFilterGroup from '../utils/TypeFilterGroup';
import WeaponTypeFilter from '../utils/WeaponTypeFilter';
import Build from '../../core/Build';
import { DISPLAY_PASSIVE_BONUSES_AVAILABLE } from '../../core/GenConfig';
import { SERVER_URL } from '../../App';
import { STATS } from '../../core/st';
import Search from '../search/Search';
import { DENSITY_SCOPE_BLACKLIST } from '../../core/searchConfig';
import HoverPanel from '../utils/HoverPanel';
import SlotTooltip from '../build/SlotTooltip';
import { calculateTooltipPosition } from '../../utils/tooltipUtils';
import { usePointerHover } from '../../hooks/usePointerHover';
import { useOverlay } from '../../contexts/OverlayContext';
import { NOTIFICATION, useNotification } from '../NotificationSystem';
import { loadItemCatalog, peekItemCatalog } from '../../services/itemCatalog';
import MediaImg from '../utils/MediaImg';
import Tooltip from '../utils/Tooltip';

const TXT_CLICK_AN_ITEM_TO_EXCLUDE_IT = { fr: 'Cliquer sur un objet pour l\'exclure', en: 'Click an item to exclude it', de: 'Auf einen Gegenstand klicken, um ihn auszuschließen', es: 'Haz clic en un objeto para excluirlo', pt: 'Clique em um item para excluí-lo' };

const TXT_FILTERS = { fr: 'Filtres', en: 'Filters', de: 'Filter', es: 'Filtros', pt: 'Filtros' };
const TXT_ALLOW_LEGENDARY = {
    fr: 'Items légendaires',
    en: 'Legendary items',
    de: 'Legendäre Gegenstände',
    es: 'Objetos legendarios',
    pt: 'Itens lendários',
};
const TXT_ALLOW_POWER = {
    fr: 'Autoriser la Puissance à contribuer aux stats élémentaires.',
    en: 'Allow Power to contribute to elemental stats.',
    de: 'Schlagkraft zu den Elementar-Stats beitragen lassen.',
    es: 'Permitir que la Potencia contribuya a las stats elementales.',
    pt: 'Permitir que a Potência contribua para as stats elementais.',
};
const TXT_CRIT_DAM_COUNTS_AS_DAMAGE = {
    fr: 'Autoriser les Dommages Critiques à contribuer aux dommages élémentaires.',
    en: 'Allow Critical Damage to contribute to elemental damage stats.',
    de: 'Kritischen Schaden zum Elementarschaden beitragen lassen.',
    es: 'Permitir que los Daños críticos contribuyan a los daños elementales.',
    pt: 'Permitir que os Danos críticos contribuam para os danos elementais.',
};
const TXT_FM_SECTION = { fr: 'Forgemagie', en: 'Smithmagic', de: 'Schmiedemagie', es: 'Forjamagia', pt: 'Forjamagia' };
const TXT_EXO_RINGS_ONLY = {
    fr: 'Forcer les exos PA et PM sur les anneaux uniquement.',
    en: 'Force AP and MP exos on rings only.',
    de: 'AP- und BP-Exos nur auf Ringen erzwingen.',
    es: 'Forzar los exos de PA y PM solo en los anillos.',
    pt: 'Forçar os exos de PA e PM apenas nos anéis.',
};
const TXT_BOOSTS_SECTION = { fr: 'Boosts', en: 'Boosts', de: 'Boosts', es: 'Boosts', pt: 'Boosts' };
const TXT_USE_BOOSTS_FOR_STATS = {
    fr: 'Utiliser les boosts pour atteindre les critères de stats',
    en: 'Use boosts to reach the stat criteria',
    de: 'Boosts zum Erreichen der Werte-Kriterien nutzen',
    es: 'Usar los boosts para alcanzar los criterios de stats',
    pt: 'Usar os boosts para atingir os critérios de stats',
};
const TXT_USE_BOOSTS_FOR_SPELLS = {
    fr: 'Prendre en compte les boosts dans le calcul des critères de sort',
    en: 'Include boosts when computing the spell criteria',
    de: 'Boosts bei der Berechnung der Zauber-Kriterien berücksichtigen',
    es: 'Tener en cuenta los boosts al calcular los criterios de hechizo',
    pt: 'Considerar os boosts no cálculo dos critérios de feitiço',
};
const TXT_DISPLAY_PASSIVE_BONUSES = {
    fr: 'Afficher les bonus passifs sur le stuff.',
    en: 'Display passive bonuses on the build.',
    de: 'Passive Boni am Build anzeigen.',
    es: 'Mostrar los bonus pasivos en la build.',
    pt: 'Exibir os bônus passivos na build.',
};
const TXT_PASSIVE_REPLACED_BY_BOOST = {
    fr: 'Remplacé par le boost choisi dans l\'onglet Boosts.',
    en: 'Replaced by the boost picked on the Boosts tab.',
    de: 'Ersetzt durch den im Boosts-Tab gewählten Boost.',
    es: 'Sustituido por el boost elegido en la pestaña Boosts.',
    pt: 'Substituído pelo boost escolhido na aba Boosts.',
};
const TXT_BLACKLIST = { fr: 'Items exclus', en: 'Blacklisted items', de: 'Ausgeschlossene Gegenstände', es: 'Objetos excluidos', pt: 'Itens excluídos' };
const TXT_OTHER = { fr: 'Autres', en: 'Other', de: 'Sonstige', es: 'Otros', pt: 'Outros' };
const TXT_PASSIVES = { fr: 'Effets passifs', en: 'Passive effects', de: 'Passive Effekte', es: 'Efectos pasivos', pt: 'Efeitos passivos' };
const TXT_PASSIVES_SUBTITLE = {
    fr: 'Aide le générateur à favoriser (ou défavoriser) certains passifs. Les valeurs par défaut reflètent l\'apport moyen estimé de chaque passif, et non son cumul maximal.',
    en: 'Helps the generator favor (or penalize) certain passives. The default values reflect each passive\'s estimated average contribution, not its maximum stacked value.',
    de: 'Hilft dem Generator, bestimmte Passiveffekte zu bevorzugen (oder zu benachteiligen). Die Standardwerte spiegeln den geschätzten durchschnittlichen Beitrag eines Passiveffekts wider, nicht seinen maximalen Gesamtwert.',
    es: 'Ayuda al generador a favorecer (o penalizar) ciertos pasivos. Los valores por defecto reflejan el aporte medio estimado de cada pasivo, no su acumulación máxima.',
    pt: 'Ajuda o gerador a favorecer (ou penalizar) certos passivos. Os valores padrão refletem a contribuição média estimada de cada passivo, não seu acúmulo máximo.',
};
const TXT_EXCLUDE = { fr: 'Exclure', en: 'Block', de: 'Ausschließen', es: 'Excluir', pt: 'Excluir' };
const TXT_BACK = { fr: 'Retour', en: 'Back', de: 'Zurück', es: 'Volver', pt: 'Voltar' };
const TXT_ADD_EXCLUDED = { fr: 'Exclure un item', en: 'Exclude an item', de: 'Gegenstand ausschließen', es: 'Excluir un objeto', pt: 'Excluir um item' };
const TXT_REMOVE = { fr: 'Retirer', en: 'Remove', de: 'Entfernen', es: 'Quitar', pt: 'Remover' };
const TXT_BLACKLIST_ADDED = {
    fr: 'Ajouté à la liste d\'exclusion',
    en: 'Added to the exclusion list',
    de: 'Zur Ausschlussliste hinzugefügt',
    es: 'Añadido a la lista de exclusión',
    pt: 'Adicionado à lista de exclusão',
};

const ITEM_ONLY_MODE = { searchItems: true, searchSets: false };
const DEFAULT_LVL = 200;
const STAT_BY_ID = new Map(STATS.map((s) => [Number(s.id), s]));

const formatItemName = (name, lang) => {
    if (!name) return '';
    if (typeof name === 'object') return name[lang] || name.fr || name.en || '';
    return String(name);
};

const formatPassiveDescription = (description, lang, value) => {
    if (!description) return '';
    const text = typeof description === 'object'
        ? (description[lang] || description.en || description.fr || '')
        : String(description);
    return text.replaceAll('{value}', String(value));
};

// --- Blacklist thumbnail ---

const BlacklistItem = ({ item, lang, onRemove, onMouseEnter, onMouseLeave }) => {
    const name = item.name
        ? (typeof item.name === 'object' ? (item.name[lang] || item.name.en || '') : item.name)
        : '';
    const removeLabel = TXT_REMOVE[lang] || TXT_REMOVE.en;
    return (
        <div
            className="blacklist-item"
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            <button
                type="button"
                className="blacklist-item-remove"
                onClick={() => onRemove(item)}
                aria-label={`${removeLabel} ${name}`.trim()}
            >
                <span className="blacklist-item-remove-slot">
                    <MediaImg className="blacklist-item-icon" src={item.icon} alt="" aria-hidden="true" />
                    <Close className="blacklist-item-remove-close" fontSize="small" />
                </span>
            </button>
        </div>
    );
};

// --- Blacklist search ---

/**
 * Always rendered inside an overlay — the panel's own when it is one (the
 * generator opens the settings that way), or one of its own on a page where the
 * panel is just a section. `onBack` is what tells the two apart: inline it
 * returns to the settings, in an overlay the overlay's own close does that.
 */
const BlacklistSearch = ({ lang, onSelect, onBack }) => (
    <div className="gen-conf-panel column left">
        <div className="blacklist-search-header row">
            <div className="blacklist-search-header-main row">
                {onBack ? (
                    <button type="button" onClick={onBack}>
                        <ArrowBack fontSize="small" />
                        <span>{TXT_BACK[lang] || TXT_BACK.en}</span>
                    </button>
                ) : null}
                <span className="blacklist-search-hint">
                    {TXT_CLICK_AN_ITEM_TO_EXCLUDE_IT[lang] || TXT_CLICK_AN_ITEM_TO_EXCLUDE_IT.en}
                </span>
            </div>
        </div>
        <Search
            lang={lang}
            onSelectItem={onSelect}
            onCardClick={onSelect}
            initialSearchMode={ITEM_ONLY_MODE}
            scrollContainerSelector=".overlay-content"
            inOverlay
            densityScope={DENSITY_SCOPE_BLACKLIST}
            disableTypeFilters
            equipLabel={TXT_EXCLUDE[lang] || TXT_EXCLUDE.en}
            equipIcon={Block}
        />
    </div>
);

// --- Main panel ---

const GenConfPanel = ({ lang = 'fr', genConfig, setGenConfig, inOverlay = true, build = null }) => {
    // Items whose passive the build picked on the Boosts tab: when stat
    // criteria use boosts, that pick is what the solver counts, so their
    // approximation below is switched off (Build.getSolverPassiveOverrides)
    // and shown as such.
    const boostedItemIds = useMemo(
        () => (genConfig.useBoostsForStats === true && typeof build?.getBoostedItemIds === 'function'
            ? build.getBoostedItemIds()
            : new Set()),
        [build, genConfig.useBoostsForStats],
    );
    const { showNotification } = useNotification();
    const overlay = useOverlay();
    const hasPointerHover = usePointerHover();
    const [localFilters, setLocalFilters] = useState(genConfig.typeFilters || {});
    const [localAllowPower, setLocalAllowPower] = useState(!!genConfig.allowPower);
    const [localCritDamCountsAsDamage, setLocalCritDamCountsAsDamage] = useState(!!genConfig.critDamCountsAsDamage);
    const [localUseBoostsForStats, setLocalUseBoostsForStats] = useState(genConfig.useBoostsForStats === true);
    const [localUseBoostsForSpells, setLocalUseBoostsForSpells] = useState(genConfig.useBoostsForSpells !== false);
    const [localAllowLegendary, setLocalAllowLegendary] = useState(genConfig.allowLegendary !== false);
    const [localExoRingsOnly, setLocalExoRingsOnly] = useState(genConfig.exoRingsOnly === true);
    const [localDisplayPassiveBonuses, setLocalDisplayPassiveBonuses] = useState(!!genConfig.displayPassiveBonuses);
    const [localBlacklist, setLocalBlacklist] = useState(genConfig.blacklist || []);
    const [localPassiveOverrides, setLocalPassiveOverrides] = useState(genConfig.passiveOverrides || {});
    const [passiveRows, setPassiveRows] = useState([]);
    // Passive rows name an item id; the icon comes from the catalog the page
    // already holds, so it needs no field of its own on the API payload.
    const [catalog, setCatalog] = useState(peekItemCatalog);
    const [searchOpen, setSearchOpen] = useState(false);
    const [tooltip, setTooltip] = useState({ item: null, anchor: null });
    const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

    const tooltipBuild = useMemo(() => new Build({ lvl: DEFAULT_LVL }), []);

    useEffect(() => {
        setLocalFilters(genConfig.typeFilters || {});
        setLocalAllowPower(!!genConfig.allowPower);
        setLocalCritDamCountsAsDamage(!!genConfig.critDamCountsAsDamage);
        setLocalUseBoostsForStats(genConfig.useBoostsForStats === true);
        setLocalUseBoostsForSpells(genConfig.useBoostsForSpells !== false);
        setLocalAllowLegendary(genConfig.allowLegendary !== false);
        setLocalExoRingsOnly(genConfig.exoRingsOnly === true);
        setLocalDisplayPassiveBonuses(!!genConfig.displayPassiveBonuses);
        setLocalBlacklist(genConfig.blacklist || []);
        setLocalPassiveOverrides(genConfig.passiveOverrides || {});
    }, [genConfig]);

    useEffect(() => {
        if (catalog) return undefined;
        let cancelled = false;
        loadItemCatalog().then((loaded) => { if (!cancelled) setCatalog(loaded); });
        return () => { cancelled = true; };
    }, [catalog]);

    useEffect(() => {
        let cancelled = false;
        fetch(`${SERVER_URL()}passive-effects/`)
            .then((res) => res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`)))
            .then((data) => {
                if (cancelled) return;
                setPassiveRows(Array.isArray(data?.passives) ? data.passives : []);
            })
            .catch(() => {
                if (!cancelled) setPassiveRows([]);
            });
        return () => { cancelled = true; };
    }, []);

    // --- Type filter handlers ---

    const handleToggleTypeFilter = (typeId) => {
        setLocalFilters(prev => ({ ...prev, [typeId]: !prev[typeId] }));
        setGenConfig(prev => prev.toggleTypeFilter(typeId));
    };


    // --- Blacklist handlers ---

    // Composed against a ref rather than the state: `showOverlay` stores the
    // element it is handed, so a handler closing over `localBlacklist` would be
    // frozen at the list as it stood when the search opened, and the second
    // item excluded would drop the first.
    const blacklistRef = useRef(localBlacklist);
    blacklistRef.current = localBlacklist;

    const commitBlacklist = useCallback((next) => {
        blacklistRef.current = next;
        setLocalBlacklist(next);
        setGenConfig(prev => prev.update('blacklist', next));
    }, [setGenConfig]);

    const handleAddToBlacklist = useCallback((item) => {
        if (item?.search_result_type === 'set') return;
        const key = item.ankama_id || item.id;
        if (!key) return;
        const current = blacklistRef.current;
        if (current.some(b => (b.ankama_id || b.id) === key)) return;
        const itemName = item.name
            ? (typeof item.name === 'object' ? (item.name[lang] || item.name.en || '') : item.name)
            : '';
        commitBlacklist([...current, item]);
        const actionLabel = TXT_BLACKLIST_ADDED[lang] || TXT_BLACKLIST_ADDED.en;
        showNotification(itemName ? `${itemName} - ${actionLabel}` : actionLabel, NOTIFICATION.SUCCESS);
    }, [commitBlacklist, lang, showNotification]);

    const handleRemoveFromBlacklist = useCallback((item) => {
        setTooltip({ item: null, anchor: null });
        const key = item.ankama_id || item.id;
        commitBlacklist(blacklistRef.current.filter(b => (b.ankama_id || b.id) !== key));
    }, [commitBlacklist]);

    /** Takes over the panel when the panel is the overlay; opens one when it is not. */
    const openBlacklistSearch = useCallback(() => {
        if (inOverlay || !overlay?.showOverlay) {
            setSearchOpen(true);
            return;
        }
        overlay.showOverlay([
            <h6>{TXT_ADD_EXCLUDED[lang] || TXT_ADD_EXCLUDED.en}</h6>,
            <BlacklistSearch lang={lang} onSelect={handleAddToBlacklist} />,
        ]);
    }, [handleAddToBlacklist, inOverlay, lang, overlay]);

    const updatePassiveOverride = useCallback((key, defaultValue, patch) => {
        setLocalPassiveOverrides(prev => {
            const current = prev?.[key] || { enabled: true, value: defaultValue };
            const nextRow = { ...current, ...patch };
            const next = { ...(prev || {}), [key]: nextRow };
            setGenConfig(cfg => cfg.update('passiveOverrides', next));
            return next;
        });
    }, [setGenConfig]);

    const handleTooltipShow = useCallback((item, e) => {
        const anchorEl = e.currentTarget.querySelector('.blacklist-item-remove-slot') || e.currentTarget;
        const rect = anchorEl.getBoundingClientRect();
        const basePos = calculateTooltipPosition(rect, { useRightEdge: true });
        const overlayBackdrop = e.currentTarget.closest('.overlay-backdrop');
        const backdropRect = overlayBackdrop ? overlayBackdrop.getBoundingClientRect() : { left: 0, top: 0 };
        const horizontalGap = 6;

        setTooltip({ item, anchor: e.currentTarget });
        setTooltipPos({
            x: basePos.x - backdropRect.left + (basePos.goLeftwards ? -horizontalGap : horizontalGap),
            y: basePos.y - backdropRect.top,
            goUpwards: basePos.goUpwards,
            goLeftwards: basePos.goLeftwards,
        });
    }, []);

    const handleTooltipHide = useCallback(() => setTooltip({ item: null, anchor: null }), []);

    useEffect(() => {
        if (hasPointerHover) return;
        setTooltip({ item: null, anchor: null });
    }, [hasPointerHover]);

    useEffect(() => {
        if (!tooltip.item) return;
        const anchorGone = tooltip.anchor && !tooltip.anchor.isConnected;
        const itemKey = tooltip.item.ankama_id || tooltip.item.id;
        const itemStillExists = localBlacklist.some(b => (b.ankama_id || b.id) === itemKey);
        if (anchorGone || !itemStillExists) {
            setTooltip({ item: null, anchor: null });
        }
    }, [tooltip, localBlacklist]);

    // --- Search view (add items to blacklist) ---

    if (searchOpen) {
        return (
            <BlacklistSearch
                lang={lang}
                onSelect={handleAddToBlacklist}
                onBack={() => setSearchOpen(false)}
            />
        );
    }

    // --- Settings view ---

    const blacklistSection = (
        <div className="gen-conf-section blacklist-section column left">
            <h6 className="gen-conf-section-title">{TXT_BLACKLIST[lang] || TXT_BLACKLIST.en}</h6>
            <div className="blacklist-items row">
                {localBlacklist.map(item => {
                    const key = item.ankama_id || item.id;
                    return (
                        <BlacklistItem
                            key={key}
                            item={item}
                            lang={lang}
                            onRemove={handleRemoveFromBlacklist}
                            onMouseEnter={hasPointerHover ? (e) => handleTooltipShow(item, e) : undefined}
                            onMouseLeave={hasPointerHover ? handleTooltipHide : undefined}
                        />
                    );
                })}
                <button
                    type="button"
                    className="blacklist-add-btn"
                    onClick={openBlacklistSearch}
                    aria-label={TXT_ADD_EXCLUDED[lang] || TXT_ADD_EXCLUDED.en}
                >
                    <Add fontSize="small" />
                </button>
            </div>
        </div>
    );

    const settingsContent = (
        <div className="gen-conf-settings column left">
            <div className="gen-conf-section column left">
                <h6 className="gen-conf-section-title">{TXT_FILTERS[lang] || TXT_FILTERS['en']}</h6>
                <div className="filters-container row left">
                    {TYPE_FILTER_ORDER.map(categoryKey => {
                        const types = GROUPED_TYPES[categoryKey];
                        if (!types) return null;
                        // Weapons are eleven types where every other category
                        // is a handful, so they get the dropdown the spells
                        // panel's weapon row uses rather than a row that
                        // expands into the middle of the filters.
                        if (categoryKey === CAT.WEAPON) {
                            return (
                                <WeaponTypeFilter
                                    key={categoryKey}
                                    typeFilters={localFilters}
                                    onToggleType={handleToggleTypeFilter}
                                    lang={lang}
                                />
                            );
                        }
                        return (
                            <TypeFilterGroup
                                key={categoryKey}
                                types={types}
                                typeFilters={localFilters}
                                onToggleTypeFilter={handleToggleTypeFilter}
                                lang={lang}
                            />
                        );
                    })}
                </div>
                <label className="checkbox-setting">
                    <input
                        type="checkbox"
                        checked={localAllowLegendary}
                        onChange={() => { setLocalAllowLegendary(v => !v); setGenConfig(prev => prev.toggle('allowLegendary')); }}
                    />
                    <span>{TXT_ALLOW_LEGENDARY[lang] || TXT_ALLOW_LEGENDARY.en}</span>
                </label>
            </div>

            {blacklistSection}

            <div className="gen-conf-section column left">
                <h6 className="gen-conf-section-title">{TXT_FM_SECTION[lang] || TXT_FM_SECTION.en}</h6>
                <div className="checkbox-settings column left">
                    <label className="checkbox-setting">
                        <input
                            type="checkbox"
                            checked={localExoRingsOnly}
                            onChange={() => { setLocalExoRingsOnly(v => !v); setGenConfig(prev => prev.update('exoRingsOnly', prev.exoRingsOnly !== true)); }}
                        />
                        <span>{TXT_EXO_RINGS_ONLY[lang] || TXT_EXO_RINGS_ONLY.en}</span>
                    </label>
                </div>
            </div>

            <div className="gen-conf-section column left">
                <h6 className="gen-conf-section-title">{TXT_BOOSTS_SECTION[lang] || TXT_BOOSTS_SECTION.en}</h6>
                <div className="checkbox-settings column left">
                    <label className="checkbox-setting">
                        <input
                            type="checkbox"
                            checked={localUseBoostsForStats}
                            onChange={() => { setLocalUseBoostsForStats(v => !v); setGenConfig(prev => prev.update('useBoostsForStats', prev.useBoostsForStats !== true)); }}
                        />
                        <span>{TXT_USE_BOOSTS_FOR_STATS[lang] || TXT_USE_BOOSTS_FOR_STATS.en}</span>
                    </label>
                    <label className="checkbox-setting">
                        <input
                            type="checkbox"
                            checked={localUseBoostsForSpells}
                            onChange={() => { setLocalUseBoostsForSpells(v => !v); setGenConfig(prev => prev.update('useBoostsForSpells', prev.useBoostsForSpells === false)); }}
                        />
                        <span>{TXT_USE_BOOSTS_FOR_SPELLS[lang] || TXT_USE_BOOSTS_FOR_SPELLS.en}</span>
                    </label>
                </div>
            </div>

            <div className="gen-conf-section passive-settings-section column left">
                <div className="gen-conf-section-heading column left">
                    <h6 className="gen-conf-section-title">{TXT_PASSIVES[lang] || TXT_PASSIVES.en}</h6>
                    <p className="passive-settings-subtitle">{TXT_PASSIVES_SUBTITLE[lang] || TXT_PASSIVES_SUBTITLE.en}</p>
                </div>
                {DISPLAY_PASSIVE_BONUSES_AVAILABLE ? (
                    <label className="checkbox-setting">
                        <input
                            type="checkbox"
                            checked={localDisplayPassiveBonuses}
                            onChange={() => { setLocalDisplayPassiveBonuses(v => !v); setGenConfig(prev => prev.toggle('displayPassiveBonuses')); }}
                        />
                        <span>{TXT_DISPLAY_PASSIVE_BONUSES[lang] || TXT_DISPLAY_PASSIVE_BONUSES.en}</span>
                    </label>
                ) : null}
                <div className="passive-settings-list column left">
                    {passiveRows.map((row) => {
                        const key = row.key;
                        const override = localPassiveOverrides?.[key] || {};
                        const boosted = boostedItemIds.has(Number(row.item_id));
                        const enabled = override.enabled !== false && !boosted;
                        const value = override.value ?? row.value ?? 0;
                        const stat = STAT_BY_ID.get(Number(row.characteristic));
                        const statLabel = stat?.label?.[lang] || stat?.label?.en || `Stat ${row.characteristic}`;
                        const itemName = formatItemName(row.item_name, lang) || `Item ${row.item_id}`;
                        const itemIcon = catalog?.itemById?.get(Number(row.item_id))?.icon || '';
                        const description = formatPassiveDescription(row.description, lang, value);
                        return (
                            <div className={`passive-setting-row ${enabled ? '' : 'is-disabled'}`} key={key}>
                                <label className="passive-setting-enabled">
                                    <input
                                        type="checkbox"
                                        checked={enabled}
                                        disabled={boosted}
                                        onChange={(e) => updatePassiveOverride(key, row.value, { enabled: e.target.checked })}
                                    />
                                    <span className="passive-setting-item-slot">
                                        {itemIcon ? (
                                            <MediaImg className="passive-setting-item-icon" src={itemIcon} alt="" aria-hidden="true" />
                                        ) : null}
                                    </span>
                                    <span className="passive-setting-name">{itemName}</span>
                                </label>
                                <span className="passive-setting-stat">
                                    <span className="passive-setting-stat-main">
                                        {stat?.icon ? (
                                            <img
                                                src={stat.icon}
                                                alt=""
                                                className="passive-setting-stat-icon"
                                                aria-hidden="true"
                                            />
                                        ) : null}
                                        <span className="passive-setting-stat-label">{statLabel}</span>
                                        {description && hasPointerHover ? (
                                            <Tooltip
                                                content={description}
                                                className="passive-setting-tooltip"
                                                wrapperClassName="passive-setting-help"
                                            >
                                                <HelpOutline fontSize="small" titleAccess={description} />
                                            </Tooltip>
                                        ) : null}
                                    </span>
                                    {/* No hover on touch, where the tooltip never opens: keep the line. */}
                                    {description && !hasPointerHover ? (
                                        <span className="passive-setting-description">{description}</span>
                                    ) : null}
                                    {boosted ? (
                                        <span className="passive-setting-description">
                                            {TXT_PASSIVE_REPLACED_BY_BOOST[lang] || TXT_PASSIVE_REPLACED_BY_BOOST.en}
                                        </span>
                                    ) : null}
                                </span>
                                <input
                                    className="passive-setting-value"
                                    type="number"
                                    step="1"
                                    value={value}
                                    disabled={!enabled}
                                    onChange={(e) => updatePassiveOverride(key, row.value, { value: Number(e.target.value) || 0 })}
                                    aria-label={`${itemName} ${statLabel}`}
                                />
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="gen-conf-section other-settings-section column left">
                <h6 className="gen-conf-section-title">{TXT_OTHER[lang] || TXT_OTHER.en}</h6>
                <div className="checkbox-settings column left">
                    <label className="checkbox-setting">
                        <input
                            type="checkbox"
                            checked={localAllowPower}
                            onChange={() => { setLocalAllowPower(v => !v); setGenConfig(prev => prev.toggle('allowPower')); }}
                        />
                        <span>{TXT_ALLOW_POWER[lang] || TXT_ALLOW_POWER.en}</span>
                    </label>
                    <label className="checkbox-setting">
                        <input
                            type="checkbox"
                            checked={localCritDamCountsAsDamage}
                            onChange={() => { setLocalCritDamCountsAsDamage(v => !v); setGenConfig(prev => prev.toggle('critDamCountsAsDamage')); }}
                        />
                        <span>{TXT_CRIT_DAM_COUNTS_AS_DAMAGE[lang] || TXT_CRIT_DAM_COUNTS_AS_DAMAGE.en}</span>
                    </label>
                </div>
            </div>
        </div>
    );

    return (
        <div className={`gen-conf-panel column left${inOverlay ? '' : ' gen-conf-panel--bare'}`}>
            {settingsContent}
            {tooltip.item && (
                <HoverPanel pos={tooltipPos}>
                    <SlotTooltip item={tooltip.item} build={tooltipBuild} lang={lang} />
                </HoverPanel>
            )}
        </div>
    );
};

export default GenConfPanel;
